import './ScenarioSatellites.scss'
import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { CloseSvg, GroupSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import * as Cesium from 'cesium'
import { StepperProps } from '../../../utils/stepper-types'
import useStaticData from '../../../hooks/useStaticData'
import useForm from '../../../hooks/useForm'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { FormGroupDetailsType } from '../../../utils/scenario-types'
import useUserData from '../../../hooks/useUserData'
import { getSatellitesCount } from '../../../../utils'
import useModal from '../../../hooks/useModal'
import useViewer from '../../../hooks/useViewer'
import { DEFAULT_ORBIT_COLOR } from '../../../../../../utils/constants'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../constants'
import {
    buildMultiGroupPreviewCzml,
    GroupPreviewParams,
    loadPreviewDataSource,
    removePreviewWithRef,
} from '../../../../../modelling/utils/orbitCalculations'
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const PERSISTENT_PREVIEW_DS = '__scenario_orbit_preview__';

const ScenarioSatellites: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {
    const { userData } = useUserData()
    const { allGroups, loading, satelliteBaseDataLoaded } = useStaticData()
    const { modals, setModals } = useModal()
    const { formData, setFormData } = useForm()
    const { viewer } = useViewer()
    const scenarioDeploymentType = useMemo(() => formData.details.scenarioDeploymentType, [formData.details.scenarioDeploymentType])
    const scenarioOperationType = useMemo(() => formData.details.scenarioOperation, [formData.details.scenarioOperation])
    const scenarioStartTime = useMemo(() => formData.details.scenarioStartTime, [formData.details.scenarioStartTime])
    const selectedGroups = useMemo(() => formData.satellites, [formData.satellites])
    const maxLimit = useMemo(() => userData?.['feature-allowed']?.['noOfSimulationsMaxLimit'], [userData])
    const showSelectSatelliteButton = useMemo(() => (getSatellitesCount(selectedGroups) < maxLimit && allGroups?.length > 0 &&
        ((scenarioOperationType === 'LEOP' && selectedGroups?.length === 0) || (scenarioOperationType === 'CONOPS' && scenarioDeploymentType === 'HARDWARE_IN_LOOP' && selectedGroups?.length === 0) ||
            (scenarioOperationType === 'CONOPS' && scenarioDeploymentType !== 'HARDWARE_IN_LOOP'))) ? true : false, [selectedGroups, allGroups, scenarioOperationType, scenarioDeploymentType])

    const selectionModalOpen = useMemo(() => modals?.configured?.name === 'satellite', [modals?.configured?.name])
    const orbitConfigModalOpen = useMemo(() => modals?.configured?.name === 'orbitConfig', [modals?.configured?.name])

    // ─── Persistent orbit preview ───
    const persistentDsRef = useRef<Cesium.CzmlDataSource | null>(null);

    const groupTypeMap = useMemo(() => {
        const m: Record<string, typeof allGroups[0]> = {};
        for (const g of allGroups) m[g.groupId] = g;
        return m;
    }, [allGroups]);

    const removePersistentPreview = useCallback(() => {
        removePreviewWithRef(viewer, persistentDsRef, PERSISTENT_PREVIEW_DS);
    }, [viewer]);

    const loadPersistentPreview = useCallback((groups: FormGroupDetailsType[]) => {
        if (!viewer || groups.length === 0) { removePersistentPreview(); return; }

        const groupParams: GroupPreviewParams[] = groups
            .filter(gp => gp.referenceOrbit)
            .map(gp => {
                const orbit = gp.referenceOrbit;
                const fullGroup = groupTypeMap[gp.groupId];
                const isPlanePhasing = gp.phasingType === 'PlanePhasing';
                const isMulti = isPlanePhasing
                    ? gp.satCount > 1
                    : (fullGroup?.groupType === 'Multi Plane' || gp.noOfPlanes > 1);
                return {
                    groupId: gp.groupId,
                    groupName: gp.groupName,
                    altitude: Number(orbit.altitude),
                    eccentricity: Number(orbit.eccentricity),
                    inclination: Number(orbit.inclination),
                    RAAN: Number(orbit.RAAN),
                    AP: Number(orbit.AP),
                    TA: Number(orbit.TA),
                    epoch: Number(orbit.epoch),
                    startTime: Number(scenarioStartTime),
                    satCount: gp.satCount,
                    noOfPlanes: isPlanePhasing ? gp.satCount : gp.noOfPlanes,
                    noOfSatellitesPerPlane: isPlanePhasing ? 1 : gp.noOfSatellitesPerPlane,
                    phasingAngle: isPlanePhasing ? 0 : gp.phasingAngle,
                    planeSeparationAngle: isPlanePhasing ? gp.phasingAngle : gp.separationAngleBetweenPlane,
                    isMultiPlane: isMulti,
                    color: gp.color,
                };
            });

        if (groupParams.length === 0) { removePersistentPreview(); return; }

        const czml = buildMultiGroupPreviewCzml(groupParams, PERSISTENT_PREVIEW_DS);
        removePersistentPreview();
        persistentDsRef.current = loadPreviewDataSource(viewer, czml, PERSISTENT_PREVIEW_DS);
    }, [viewer, removePersistentPreview, groupTypeMap, scenarioStartTime]);

    // Draw/update orbit preview whenever formData.satellites changes
    // Skip when satellite selection or orbit config modals are open (they manage their own preview)
    useEffect(() => {
        if (selectionModalOpen || orbitConfigModalOpen) return;

        // Check if the permanent ORBIT_OBJECT already exists
        if (viewer) {
            let hasOrbitObject = false;
            for (let i = 0; i < viewer.dataSources.length; i++) {
                if (viewer.dataSources.get(i).name === 'ORBIT_OBJECT') {
                    hasOrbitObject = true;
                    break;
                }
            }
            // If permanent orbit data source exists, don't show preview
            if (hasOrbitObject) {
                removePersistentPreview();
                return;
            }
        }

        if (selectedGroups && selectedGroups.length > 0) {
            loadPersistentPreview(selectedGroups);
        } else {
            removePersistentPreview();
        }
    }, [viewer, selectedGroups, selectionModalOpen, orbitConfigModalOpen, loadPersistentPreview, removePersistentPreview]);

    // Cleanup on unmount
    useEffect(() => {
        return () => { removePersistentPreview(); };
    }, [removePersistentPreview]);

    const openSelectionModal = () => {
        const selectedGroups = formData.satellites || []
        const group = selectedGroups[0]
        const satellite = (scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP && group) ? {
            name: group?.satelliteName,
            uniqueId: group?.satelliteId
        } : null
        setModals((prev) => ({
            ...prev,
            configured: {
                name: 'satellite',
                open: true,
                data: {
                    checkedGroups: selectedGroups,
                    checkedSat: satellite
                }
            }
        }))
    }

    const removeGroup = (gp: FormGroupDetailsType) => {
        const filterGroups = selectedGroups?.filter(g => g?.groupId !== gp?.groupId)
        setFormData((prev) => ({
            ...prev,
            satellites: filterGroups
        }))
    }

    const openConfigForm = (gp: FormGroupDetailsType) => {
        setModals((prev) => ({
            ...prev,
            configured: {
                name: 'orbitConfig',
                open: true,
                data: gp
            }
        }))
    }


    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                <div className='satellite__compnents__section'>
                    <div className='compnents__section__body'>
                        {selectedGroups?.length > 0 && (selectedGroups as FormGroupDetailsType[])?.map((gp) => (
                            <div className='compnents__section__list__item__container' key={gp?.groupId}>
                                <div className='list__item__text__container'>
                                    <div className='list__color__container' style={{ background: (gp?.color !== '' && !gp?.color.includes('#')) ? `rgba(${JSON.parse(gp?.color).join(",")})` : `rgba(${DEFAULT_ORBIT_COLOR.join(",")})` }} />
                                    <div className='list__item__text'>
                                        <CustomToolTip title={scenarioDeploymentType !== 'HARDWARE_IN_LOOP' ? gp.groupName : gp?.satelliteName} placement='top-start'>
                                            {scenarioDeploymentType !== 'HARDWARE_IN_LOOP' ? gp.groupName : gp?.satelliteName}
                                        </CustomToolTip>
                                    </div>
                                </div>
                                <div className='list__item__button__container'>
                                    <div className='action__button'>
                                        {gp?.satCount}
                                    </div>
                                    <CustomToolTip title='Configure' placement='top'>
                                        <button id={gp?.groupId} className='satellite__config__button modal__trigger__button' onClick={() => openConfigForm(gp)}>
                                            <GroupSvg />
                                        </button>
                                    </CustomToolTip>
                                    <CustomToolTip title='Remove' placement='top'>
                                        <button className='action__button' onClick={() => removeGroup(gp)}>
                                            <CloseSvg />
                                        </button>
                                    </CustomToolTip>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {loading?.base
                    ? <div className='empty__information__section'>Loading satellites...</div>
                    : allGroups?.length === 0 && <div className='empty__information__section'>No Satellite Groups available</div>
                }
                {!loading?.base && showSelectSatelliteButton &&
                    <button className={`satellite__modal__open__button modal__trigger__button`} disabled={selectionModalOpen} onClick={openSelectionModal}>
                        Select Satellites
                    </button>
                }
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}

export default ScenarioSatellites
